import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { api, eur } from "@/lib/api";
import { ProductThumb, sizeLabel } from "@/components/ProductThumb";
import { SizeText } from "@/components/RichText";

export function SearchBar({ mobile = false, onDone }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const navigate = useNavigate();
  const loc = useLocation();
  const box = useRef(null);

  useEffect(() => { setOpen(false); setQ(""); }, [loc.pathname]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); return; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => api.get("/products", { params: { q: term, limit: 8 } })
      .then((r) => { if (alive) { setRes(r.data); setHi(-1); } })
      .finally(() => alive && setBusy(false)), 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (p) => { navigate(`/timbro/${p.slug}`); setOpen(false); onDone?.(); };
  const all = () => { if (q.trim()) { navigate(`/catalogo?q=${encodeURIComponent(q.trim())}`); setOpen(false); onDone?.(); } };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min((res?.length || 0) - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(-1, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); hi >= 0 && res?.[hi] ? go(res[hi]) : all(); }
    else if (e.key === "Escape") { setOpen(false); onDone?.(); }
  };

  return (
    <div ref={box} className="relative w-full" data-testid={mobile ? "mobile-search" : "header-search"}>
      <div className="flex items-center h-10 rounded-full border border-slate-300 bg-white focus-within:border-brand-navy focus-within:ring-2 focus-within:ring-brand-yellow/60 px-3 gap-2">
        {busy ? <Loader2 className="w-4 h-4 text-slate-400 animate-spin shrink-0" /> : <Search className="w-4 h-4 text-slate-400 shrink-0" />}
        <input data-testid={mobile ? "mobile-search-input" : "header-search-input"} value={q} autoFocus={mobile} onKeyDown={onKey}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Cerca modello, codice, misura…" title="Es. 4912, TR4912, 14x38" className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none" aria-label="Cerca prodotti" />
        {q && <button data-testid="search-clear-btn" onClick={() => { setQ(""); setRes(null); }} className="text-slate-400 hover:text-brand-navy"><X className="w-4 h-4" /></button>}
      </div>
      {open && res && (
        <div className="absolute left-0 right-0 md:right-auto md:w-[480px] top-12 z-50 rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden" data-testid="search-results">
          {res.length === 0 ? <div className="p-4 text-sm text-slate-500" data-testid="search-no-results">Nessun prodotto trovato per "{q}"</div> : (
            <>
              {res.map((p, i) => (
                <button key={p.id} data-testid={`search-result-${p.slug}`} onMouseEnter={() => setHi(i)} onClick={() => go(p)}
                  className={`w-full flex items-center gap-3 p-2.5 text-left border-b border-slate-100 ${hi === i ? "bg-brand-yellow/25" : "bg-white"}`}>
                  <div className="w-16 h-12 rounded ink-grid flex items-center justify-center shrink-0 overflow-hidden"><ProductThumb p={p} w={56} h={40} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-snug line-clamp-2"><SizeText text={p.name} /></div>
                    <div className="text-[11px] font-mono text-slate-500 truncate">{p.sku ? `Cod. ${p.sku} · ` : ""}{p.kind === "pad" ? "Cuscinetto" : sizeLabel(p)}</div>
                  </div>
                  <div className="text-sm font-bold text-brand-navy shrink-0">{eur(p.price)}</div>
                </button>
              ))}
              <button data-testid="search-see-all-btn" onClick={all} className="w-full p-2.5 text-sm font-semibold text-brand-navy hover:bg-slate-50">Vedi tutti i risultati</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
